"use client";

import { useState } from "react";
import Button from "@/components/ui/Button";
import { Check, Minus } from "lucide-react";
import { APP_LOGIN_URL } from "@/lib/site-config";

type BillingCycle = "monthly" | "semi-annual" | "annual";

interface Tier {
  name: string;
  description: string;
  price: { monthly: number | null; semiAnnual: number | null; annual: number | null };
  originalPrice: { semiAnnual: number | null; annual: number | null };
  requestsPerDay: string;
  requestsPerMonth: { monthly: string; semiAnnual: string; annual: string };
  requestsPerSec: { monthly: number; semiAnnual: number; annual: number };
  bestFor: string;
  highlighted: boolean;
  cta: string;
  href: string;
  features: { label: string; included: boolean }[];
}

const tiers: Tier[] = [
  {
    name: "Free",
    description: "For testing, research, and early builds. No cost, no commitment.",
    price: { monthly: 0, semiAnnual: 0, annual: 0 },
    originalPrice: { semiAnnual: null, annual: null },
    requestsPerDay: "20K",
    requestsPerMonth: { monthly: "600K", semiAnnual: "2.4M", annual: "4.8M" },
    requestsPerSec: { monthly: 40, semiAnnual: 40, annual: 40 },
    bestFor: "Research & POC",
    highlighted: false,
    cta: "Deploy Free",
    href: APP_LOGIN_URL,
    features: [
      { label: "Developer community support", included: true },
      { label: "50+ blockchains", included: true },
      { label: "Real-time dashboard", included: true },
      { label: "Archive node access", included: false },
    ],
  },
  {
    name: "Build",
    description: "For protocols and dApps going live. Reliable endpoints from day one.",
    price: { monthly: 29, semiAnnual: 149, annual: 299 },
    originalPrice: { semiAnnual: 174, annual: 348 },
    requestsPerDay: "60K",
    requestsPerMonth: { monthly: "1.5M", semiAnnual: "9M", annual: "10.8M" },
    requestsPerSec: { monthly: 60, semiAnnual: 60, annual: 60 },
    bestFor: "Early-Stage Protocols",
    highlighted: false,
    cta: "Get Started",
    href: APP_LOGIN_URL,
    features: [
      { label: "Email support, 24h response", included: true },
      { label: "50+ blockchains", included: true },
      { label: "Usage analytics dashboard", included: true },
      { label: "Archive node access", included: false },
    ],
  },
  {
    name: "Basic",
    description: "For protocols that need steady throughput and a guaranteed uptime SLA.",
    price: { monthly: 79, semiAnnual: 419, annual: 849 },
    originalPrice: { semiAnnual: 474, annual: 948 },
    requestsPerDay: "500K",
    requestsPerMonth: { monthly: "15M", semiAnnual: "90M", annual: "150M" },
    requestsPerSec: { monthly: 160, semiAnnual: 160, annual: 220 },
    bestFor: "Growing Protocols",
    highlighted: true,
    cta: "Get Started",
    href: APP_LOGIN_URL,
    features: [
      { label: "Priority support, 4h response SLA", included: true },
      { label: "Archive node access", included: true },
      { label: "99.95% uptime SLA", included: true },
      { label: "Usage analytics dashboard", included: true },
    ],
  },
  {
    name: "Advanced",
    description: "For funds, institutions, and protocols running at full production scale.",
    price: { monthly: 169, semiAnnual: 959, annual: 1850 },
    originalPrice: { semiAnnual: 1014, annual: 2014 },
    requestsPerDay: "1.6M",
    requestsPerMonth: { monthly: "50M", semiAnnual: "50M", annual: "50M" },
    requestsPerSec: { monthly: 220, semiAnnual: 220, annual: 220 },
    bestFor: "Production & Institutional",
    highlighted: false,
    cta: "Speak With Sales",
    href: "/contact-us",
    features: [
      { label: "Dedicated engineering Slack channel", included: true },
      { label: "Dedicated IP addresses", included: true },
      { label: "Custom rate limits & throughput", included: true },
      { label: "Archive node access", included: true },
    ],
  },
];

function formatPrice(cents: number | null): string {
  if (cents === null) return "—";
  if (cents === 0) return "$0";
  return `$${cents.toLocaleString('en-US')}`;
}

function cycleLabel(cycle: BillingCycle): string {
  if (cycle === "monthly") return "/mo";
  if (cycle === "semi-annual") return "/6 mo";
  return "/yr";
}

function cyclePeriodLabel(cycle: BillingCycle): string {
  if (cycle === "monthly") return "/ mo";
  if (cycle === "semi-annual") return "6-mo total";
  return "yr total";
}

function getPrice(tier: Tier, cycle: BillingCycle): number | null {
  if (cycle === "monthly") return tier.price.monthly;
  if (cycle === "semi-annual") return tier.price.semiAnnual;
  return tier.price.annual;
}

function getOriginal(tier: Tier, cycle: BillingCycle): number | null {
  if (cycle === "monthly") return null;
  if (cycle === "semi-annual") return tier.originalPrice.semiAnnual;
  return tier.originalPrice.annual;
}

function getReqMonth(tier: Tier, cycle: BillingCycle): string {
  if (cycle === "monthly") return tier.requestsPerMonth.monthly;
  if (cycle === "semi-annual") return tier.requestsPerMonth.semiAnnual;
  return tier.requestsPerMonth.annual;
}

function getReqSec(tier: Tier, cycle: BillingCycle): number {
  if (cycle === "monthly") return tier.requestsPerSec.monthly;
  if (cycle === "semi-annual") return tier.requestsPerSec.semiAnnual;
  return tier.requestsPerSec.annual;
}

export default function PricingTiers() {
  const [cycle, setCycle] = useState<BillingCycle>("monthly");

  const cycles: { value: BillingCycle; label: string }[] = [
    { value: "monthly", label: "Monthly" },
    { value: "semi-annual", label: "6-Month" },
    { value: "annual", label: "Annual" },
  ];

  const isExtended = cycle !== "monthly";

  return (
    <div>
      {/* Billing toggle */}
      <div className="mb-12 flex flex-col items-center gap-3">
        <div className="inline-flex rounded-full border border-border-subtle bg-bg-elevated p-1" role="tablist" aria-label="Billing cycle">
          {cycles.map((c) => (
            <button
              key={c.value}
              role="tab"
              aria-selected={cycle === c.value}
              onClick={() => setCycle(c.value)}
              className={`focus-ring relative rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-200 ${
                cycle === c.value
                  ? "bg-brand-purple text-white"
                  : "text-text-secondary hover:text-white"
              }`}
            >
              {c.label}
              {c.value === "annual" && (
                <span className="absolute -right-1 -top-2.5 rounded-full bg-brand-purple-light px-1.5 py-0.5 text-[9px] font-bold text-white">
                  -14%
                </span>
              )}
            </button>
          ))}
        </div>
        {isExtended && (
          <span className="animate-[fadeIn_0.3s_ease-out] rounded-full bg-brand-purple/10 px-3 py-1 text-xs font-semibold text-brand-purple-light">
            Save up to 14% on longer plans
          </span>
        )}
      </div>

      {/* Tier cards */}
      <div className="grid items-start gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {tiers.map((tier) => {
          const price = getPrice(tier, cycle);
          const original = getOriginal(tier, cycle);

          return (
            <div
              key={tier.name}
              className={`relative flex flex-col rounded-2xl border p-7 transition-all duration-200 ${
                tier.highlighted
                  ? "border-brand-purple/40 bg-bg-card lg:-mt-2 lg:mb-2"
                  : "border-border-subtle bg-bg-card hover:border-ink/[0.18]"
              }`}
              style={
                tier.highlighted
                  ? {
                      backgroundImage:
                        "linear-gradient(to bottom, var(--color-bg-card), color-mix(in oklab, var(--accent) 5%, var(--color-bg-card)))",
                      boxShadow:
                        "0 12px 40px -16px color-mix(in oklab, var(--accent) 35%, transparent)",
                    }
                  : undefined
              }
            >
              {/* Most Popular flag — flush tab attached to the card's top edge,
                  not a floating pill */}
              {tier.highlighted && (
                <span className="absolute -top-px left-6 rounded-b-[var(--radius-sm)] bg-brand-purple-light px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                  Recommended
                </span>
              )}

              {/* Header */}
              <h2 className="mb-1 text-xl font-bold text-white">{tier.name}</h2>
              <p className="mb-1 text-xs font-medium text-text-muted">{tier.bestFor}</p>

              {/* Price */}
              <div className="mb-5 mt-4">
                {original !== null && (
                  <p className="text-sm text-text-muted line-through">{formatPrice(original)}</p>
                )}
                <div className="flex items-baseline gap-1">
                  <span className={`font-bold tracking-tight ${tier.highlighted ? "text-4xl text-white" : "text-3xl text-white"}`}>
                    {formatPrice(price)}
                  </span>
                  {price !== 0 && price !== null && (
                    <span className="text-sm font-medium text-text-muted">{cycleLabel(cycle)}</span>
                  )}
                </div>
              </div>

              {/* Quota metrics */}
              <div className="mb-5 grid grid-cols-3 divide-x divide-border-subtle rounded-xl border border-border-subtle bg-bg-elevated">
                <div className="flex flex-col items-center gap-0.5 px-2 py-3">
                  <span className="text-[9px] font-semibold uppercase tracking-wider text-text-muted">/ day</span>
                  <span className="text-sm font-bold text-white">{tier.requestsPerDay}</span>
                </div>
                <div className="flex flex-col items-center gap-0.5 px-2 py-3">
                  <span className="text-[9px] font-semibold uppercase tracking-wider text-text-muted">{cyclePeriodLabel(cycle)}</span>
                  <span className="text-sm font-bold text-white">{getReqMonth(tier, cycle)}</span>
                </div>
                <div className="flex flex-col items-center gap-0.5 px-2 py-3">
                  <span className="text-[9px] font-semibold uppercase tracking-wider text-text-muted">req/sec</span>
                  <span className="text-sm font-bold text-white">{getReqSec(tier, cycle)}</span>
                </div>
              </div>

              {/* Divider */}
              <div className="mb-5 h-px bg-border-subtle" />

              {/* Features */}
              <ul className="mb-7 flex flex-grow flex-col gap-3">
                {tier.features.map((f) => (
                  <li key={f.label} className="flex items-start gap-2.5 text-sm">
                    {f.included ? (
                      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-purple/15">
                        <Check size={10} className="text-brand-purple-light" strokeWidth={3} />
                      </span>
                    ) : (
                      <Minus size={14} className="mt-0.5 shrink-0 text-ink/30" />
                    )}
                    <span className={f.included ? "text-text-secondary" : "text-ink/45 line-through decoration-ink/20"}>
                      {f.label}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <div className="mt-auto">
                <Button
                  href={tier.href}
                  variant={tier.highlighted ? "primary" : "outline"}
                  className="w-full justify-center"
                  target={tier.href.startsWith("http") ? "_blank" : undefined}
                  rel={tier.href.startsWith("http") ? "noopener noreferrer" : undefined}
                >
                  {tier.cta}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
